import { getSessionExclusiveSiteIds } from './sessionEligibilityService'
import { sessionDetailsService } from './sessionDetailsService'
import { clearCheckoutDraft } from '@/lib/checkoutDraft'
import { getGroupDiscountPrice, getSessionGroupDiscountTiers, type GroupDiscountTier } from '@/lib/groupDiscount'

export interface SessionPromoCode {
  promotionalCode: string
  promotionalFee: number
}

export interface RegistrationTarget {
  userId?: number
  sid?: string
  email: string
  name: string
  orgId?: number
  siteId?: number
  locationId?: number
}

export interface CartItem {
  sessionId: number
  eventId?: number
  title: string
  subtitle?: string
  /** Price charged per participant: the base fee, or the group discount rate when a tier applies */
  fee: number
  /** Regular (non-discounted) fee — used for display when early reg was applied */
  originalFee?: number
  /** Resolved fee at add-to-cart time (early fee or regular/membership fee), before group discounts */
  baseFee?: number
  /** Session group discount tiers; the count of this session's cart items selects the tier */
  groupDiscounts?: GroupDiscountTier[]
  /** Minimum participants of the group discount tier currently applied to `fee` */
  groupDiscountMinParticipants?: number
  /** Early registration fee amount (null when session has no early reg) */
  earlyFee?: number | null
  /** ISO date string: early fee valid on or before this date */
  earlyFeeDeadline?: string | null
  startDate: string
  endDate?: string
  location?: string
  credits?: string
  addedAt: string
  description?: string
  isOnDemand?: boolean
  onlineTypeId?: number
  lmsTypeId?: number
  allowPO?: boolean
  requireShippingAddress?: boolean
  exclusiveSiteIds?: number[]
  requiresScormRegistration?: boolean
  registrationTarget?: RegistrationTarget
  promoCodes?: SessionPromoCode[]
  appliedPromoCode?: string
  appliedPromoFee?: number
  schedule?: Array<{
    startDate: string
    endDate: string
    location_id: number
    locationName?: string
    locationAddress?: {
      street1: string
      street2: string | null
      city: string
      state: string
      zip: string
      phone: string
      email: string
    } | null
  }>
}

export interface Cart {
  items: CartItem[]
  totalItems: number
  totalFee: number
  /**
   * Participants per session already paid for after a partially failed checkout. They still
   * count toward group discount tiers so retried attendees keep the group's rate.
   */
  paidGroupParticipants?: Record<string, number>
}

const CART_STORAGE_KEY = 'escworks-cart'

function getRegistrationTargetKey(target?: RegistrationTarget) {
  return target?.userId ?? target?.sid ?? target?.email ?? null
}

export function getGroupParticipantCount(cart: Cart, sessionId: number): number {
  const inCart = cart.items.filter(item => item.sessionId === sessionId).length
  return inCart + (cart.paidGroupParticipants?.[sessionId] ?? 0)
}

function applyGroupDiscounts(cart: Cart): void {
  if (cart.items.length === 0) delete cart.paidGroupParticipants

  for (const item of cart.items) {
    if (!item.groupDiscounts?.length) continue

    const baseFee = item.baseFee ?? item.fee
    const { fee, tier } = getGroupDiscountPrice(
      baseFee,
      item.groupDiscounts,
      getGroupParticipantCount(cart, item.sessionId),
    )
    item.baseFee = baseFee
    item.fee = fee
    if (tier) {
      item.groupDiscountMinParticipants = tier.minParticipants
    } else {
      delete item.groupDiscountMinParticipants
    }
  }
}

function recalculateTotals(cart: Cart): void {
  applyGroupDiscounts(cart)
  cart.totalItems = cart.items.length
  cart.totalFee = cart.items.reduce((sum, item) => {
    if (item.appliedPromoCode != null && item.appliedPromoFee != null) {
      return sum + item.appliedPromoFee
    }
    return sum + item.fee
  }, 0)
}

function resolveSessionFees(sessionDetails: any) {
  const regularFee: number = sessionDetails.basic.fee
  const earlyFee: number | null = sessionDetails.basic.fee_early ?? null
  const earlyFeeDeadline: string | null = sessionDetails.basic.regEarly ?? null
  // The session API lowers `fee` to the signed-in participant's district membership price but
  // leaves `fee_early` alone, so the early fee only applies while it is still the lower price.
  const isEarlyActive =
    earlyFee !== null &&
    earlyFeeDeadline !== null &&
    new Date() <= new Date(earlyFeeDeadline) &&
    earlyFee < regularFee
  const groupDiscounts = getSessionGroupDiscountTiers(sessionDetails)

  return {
    fee: isEarlyActive ? earlyFee! : regularFee,
    originalFee: regularFee,
    earlyFee,
    earlyFeeDeadline,
    groupDiscounts: groupDiscounts.length > 0 ? groupDiscounts : undefined,
  }
}

function normalizeAllowPO(sessionDetails: any): boolean {
  const basic = sessionDetails?.basic ?? {}
  const allowPOValue = basic.allowPO ?? basic.allowpo ?? basic.allow_po ?? basic['allow-po']

  if (typeof allowPOValue === 'string') {
    return allowPOValue.trim().toLowerCase() !== 'false' && allowPOValue.trim() !== '0'
  }

  return allowPOValue !== false && allowPOValue !== 0
}

export function sessionRequiresShippingAddress(sessionDetails: any): boolean {
  const value =
    sessionDetails?.['require-shipping-address'] ??
    sessionDetails?.requireShippingAddress ??
    sessionDetails?.basic?.['require-shipping-address'] ??
    sessionDetails?.basic?.requireShippingAddress

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === 'true' || normalized === '1'
  }

  return value === true || value === 1
}

export const cartService = {
  // Get cart from session storage
  getCart: (): Cart => {
    try {
      const cartData = sessionStorage.getItem(CART_STORAGE_KEY)
      if (cartData) {
        return JSON.parse(cartData)
      }
    } catch (error) {
      console.error('Error reading cart from storage:', error)
    }
    
    return {
      items: [],
      totalItems: 0,
      totalFee: 0
    }
  },

  // Save cart to session storage
  saveCart: (cart: Cart): void => {
    if (cart.items.length === 0) clearCheckoutDraft()
    try {
      sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart))
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('cartUpdated'))
    } catch (error) {
      console.error('Error saving cart to storage:', error)
    }
  },

  // Add item to cart
  addToCart: (
    sessionDetails: any,
    requiresScormRegistration = false,
    gridTitle?: string,
    registrationTarget?: RegistrationTarget
  ): { cart: Cart; success: boolean; message: string } => {
    const cart = cartService.getCart()
    
    // Check if item already exists in cart
    const targetKey = getRegistrationTargetKey(registrationTarget)
    const existingItemIndex = cart.items.findIndex(item =>
      item.sessionId === sessionDetails.sessionid && getRegistrationTargetKey(item.registrationTarget) === targetKey
    )
    
    if (existingItemIndex >= 0) {
      // Item already exists, don't add duplicate
      return {
        cart,
        success: false,
        message: 'This session is already in your cart'
      }
    } else {
      // Add new item
      const onlineTypeId = sessionDetails.basic.Online_Type_ID
      const lmsTypeId = sessionDetails.basic.LMSTypeID
      const isOnDemand = onlineTypeId > 0 || lmsTypeId > 0 // On-demand if it has an online type or LMS type
      const shouldRegisterInScorm = requiresScormRegistration || onlineTypeId === -61
      
      // Use the title from the grid if provided, otherwise use subTitle
      const itemTitle = gridTitle || sessionDetails.basic.subTitle || `UnknownTitle-${sessionDetails.sessionid}`
      
      console.log('📋 Cart item title:', {
        gridTitle,
        subTitle: sessionDetails.basic.subTitle,
        finalTitle: itemTitle
      })
      
      if (!gridTitle && !sessionDetails.basic.subTitle) {
        console.warn('⚠️ WARNING: Session has no title! This will create incorrect SCORM courseId')
      }
      
      // Normalize promo codes from either SessionDetails (promo_codes) or OndemandSession (promoCodes) shape
      const rawPromos: SessionPromoCode[] = (
        sessionDetails.promo_codes || sessionDetails.promoCodes || []
      ).map((p: any) => ({
        promotionalCode: (p.promotionalCode || p.code || '').toString().trim().toUpperCase(),
        promotionalFee: Number(p.promotionalFee ?? p.fee ?? 0)
      })).filter((p: SessionPromoCode) => p.promotionalCode.length > 0)

      const exclusiveSiteIds = getSessionExclusiveSiteIds(sessionDetails)
      const fees = resolveSessionFees(sessionDetails)

      const newItem: CartItem = {
        sessionId: sessionDetails.sessionid,
        eventId: sessionDetails.basic.event_id,
        title: itemTitle,
        subtitle: sessionDetails.basic.subTitle ? `Session ${sessionDetails.sessionid}` : undefined,
        fee: fees.fee,
        originalFee: fees.originalFee,
        baseFee: fees.fee,
        groupDiscounts: fees.groupDiscounts,
        earlyFee: fees.earlyFee,
        earlyFeeDeadline: fees.earlyFeeDeadline,
        startDate: sessionDetails.schedule?.[0]?.startDate || new Date().toISOString(),
        endDate: sessionDetails.schedule?.[0]?.endDate,
        location: sessionDetails.schedule?.[0]?.location_id ? `Location ${sessionDetails.schedule[0].location_id}` : undefined,
        credits: sessionDetails.credits?.[0]?.creditDisplay || 'Not specified',
        addedAt: new Date().toISOString(),
        isOnDemand,
        onlineTypeId,
        lmsTypeId,
        allowPO: normalizeAllowPO(sessionDetails),
        requireShippingAddress: sessionRequiresShippingAddress(sessionDetails),
        exclusiveSiteIds: exclusiveSiteIds.length > 0 ? exclusiveSiteIds : undefined,
        requiresScormRegistration: shouldRegisterInScorm,
        registrationTarget,
        promoCodes: rawPromos.length > 0 ? rawPromos : undefined,
        schedule: sessionDetails.schedule || []
      }
      
      console.log('🛒 Adding to cart with requiresScormRegistration:', shouldRegisterInScorm)
      console.log('🏷️ Available promo codes for session', sessionDetails.sessionid, ':', rawPromos.length > 0 ? rawPromos : 'None')
      console.log('🏷️ Raw promo_codes from API:', sessionDetails.promo_codes || sessionDetails.promoCodes || '(empty)')
      
      cart.items.push(newItem)
      
      recalculateTotals(cart)
      cartService.saveCart(cart)
      
      return {
        cart,
        success: true,
        message: 'Session added to cart successfully'
      }
    }
  },

  // Remove item from cart. Pass `paid` for attendees already charged in this checkout so the
  // remaining participants keep the group discount tier they were priced at.
  removeFromCart: (sessionId: number, registrationTarget?: RegistrationTarget, options?: { paid?: boolean }): Cart => {
    const cart = cartService.getCart()
    const targetKey = getRegistrationTargetKey(registrationTarget)
    const remainingItems = cart.items.filter(item =>
      !(item.sessionId === sessionId && getRegistrationTargetKey(item.registrationTarget) === targetKey)
    )

    const removedCount = cart.items.length - remainingItems.length
    if (options?.paid && removedCount > 0) {
      cart.paidGroupParticipants = {
        ...cart.paidGroupParticipants,
        [sessionId]: (cart.paidGroupParticipants?.[sessionId] ?? 0) + removedCount,
      }
    }
    cart.items = remainingItems
    
    recalculateTotals(cart)
    cartService.saveCart(cart)
    return cart
  },

  // The participant's own items are priced when added, possibly before sign-in or before their
  // district was saved. Re-read them so the cart shows the price the session API returns for the
  // current district. Items registered for someone else keep the price they were added at.
  refreshOwnItemPricing: async (): Promise<Cart> => {
    const sessionIds = [...new Set(
      cartService.getCart().items.filter(item => !item.registrationTarget).map(item => item.sessionId),
    )]
    const pricing = new Map<number, ReturnType<typeof resolveSessionFees>>()
    await Promise.all(sessionIds.map(async sessionId => {
      try {
        pricing.set(sessionId, resolveSessionFees(await sessionDetailsService.getSessionDetails(sessionId)))
      } catch {
        // Keep the price the item was added at.
      }
    }))

    // Items may have been added or removed while the session details loaded.
    const cart = cartService.getCart()
    const before = JSON.stringify(cart)
    for (const item of cart.items) {
      const fees = item.registrationTarget ? undefined : pricing.get(item.sessionId)
      if (!fees) continue

      item.fee = fees.fee
      item.baseFee = fees.fee
      item.originalFee = fees.originalFee
      item.earlyFee = fees.earlyFee
      item.earlyFeeDeadline = fees.earlyFeeDeadline
      if (fees.groupDiscounts) {
        item.groupDiscounts = fees.groupDiscounts
      } else {
        delete item.groupDiscounts
        delete item.groupDiscountMinParticipants
      }
    }

    recalculateTotals(cart)
    if (JSON.stringify(cart) === before) return cart
    cartService.saveCart(cart)
    return cart
  },

  // Apply a promo code across all cart items that support it.
  // Returns which sessions it was applied to (or an error message).
  applyPromoCode: (code: string): { cart: Cart; appliedTo: number[]; message: string } => {
    if (!code || !code.trim()) {
      return { cart: cartService.getCart(), appliedTo: [], message: 'Please enter a promo code' }
    }

    const normalizedCode = code.trim().toUpperCase()
    const cart = cartService.getCart()
    const appliedTo: number[] = []

    console.log('🏷️ Attempting to apply promo code:', normalizedCode)
    console.log('🏷️ Cart items and their available promo codes:')
    for (const item of cart.items) {
      console.log(`   Session ${item.sessionId}:`, item.promoCodes || 'No promo codes')
    }

    for (const item of cart.items) {
      if (!item.promoCodes || item.promoCodes.length === 0) continue

      const match = item.promoCodes.find(p => p.promotionalCode === normalizedCode)
      if (match) {
        console.log(`   ✅ Match on session ${item.sessionId}: $${item.fee} → $${match.promotionalFee}`)
        item.appliedPromoCode = normalizedCode
        item.appliedPromoFee = match.promotionalFee
        appliedTo.push(item.sessionId)
      }
    }

    if (appliedTo.length === 0) {
      console.log('   ❌ No matching sessions for code:', normalizedCode)
      return { cart, appliedTo: [], message: 'Invalid promo code. Please check and try again.' }
    }

    recalculateTotals(cart)
    cartService.saveCart(cart)

    const sessionLabel = appliedTo.length === 1 ? '1 session' : `${appliedTo.length} sessions`
    return { cart, appliedTo, message: `Promo code "${normalizedCode}" applied to ${sessionLabel}` }
  },

  // Remove an applied promo code from all cart items
  removePromoCode: (): Cart => {
    const cart = cartService.getCart()
    for (const item of cart.items) {
      delete item.appliedPromoCode
      delete item.appliedPromoFee
    }
    recalculateTotals(cart)
    cartService.saveCart(cart)
    return cart
  },

  // Get the effective fee for a cart item (promo price if applied, otherwise original)
  getEffectiveFee: (item: CartItem): number => {
    if (item.appliedPromoCode != null && item.appliedPromoFee != null) {
      return item.appliedPromoFee
    }
    return item.fee
  },

  // The promo code applied to a cart item and the item's fee before it, for recording with the registration
  getItemPromo: (item: CartItem): { promoCode: string; originalFee: number } | undefined => {
    if (item.appliedPromoCode == null || item.appliedPromoFee == null) return undefined
    return { promoCode: item.appliedPromoCode, originalFee: item.fee }
  },

  isGroupDiscountApplied: (item: CartItem): boolean => {
    return (
      item.groupDiscountMinParticipants != null &&
      item.baseFee != null &&
      item.fee < item.baseFee
    )
  },

  // Returns true if this item was added at an early registration price
  isEarlyRegApplied: (item: CartItem): boolean => {
    return (
      item.earlyFee != null &&
      item.originalFee != null &&
      item.fee === item.earlyFee &&
      item.earlyFee < item.originalFee
    )
  },

  // Clear entire cart
  clearCart: (): Cart => {
    const emptyCart = {
      items: [],
      totalItems: 0,
      totalFee: 0
    }
    
    cartService.saveCart(emptyCart)
    return emptyCart
  },

  // Check if item is in cart
  isInCart: (sessionId: number): boolean => {
    const cart = cartService.getCart()
    return cart.items.some(item => item.sessionId === sessionId)
  },

  // Get cart count for header display
  getCartCount: (): number => {
    const cart = cartService.getCart()
    return cart.totalItems
  }
}
